<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import type { FindContainersSite } from '@gpx-spa/queries/src/getAccountFindContainers';
import {
  formatSiteDistance,
  formatTarget,
  stockBar,
  stockBarScale,
  type FindContainersDistanceUnit,
  type StockLevel,
} from '@/utils/find-containers';

const FILL_CLASS: Record<StockLevel, string> = {
  'over-max': 'bg-gpx_yellow-200',
  'under-min': 'bg-gpx_red-200',
  'in-range': 'bg-gpx_gray-300',
};

const props = defineProps<{
  sites: FindContainersSite[];
  unit: FindContainersDistanceUnit;
  selectedGeofenceId: number | null;
}>();

const emit = defineEmits<{
  select: [site: FindContainersSite];
}>();

const list = ref<HTMLUListElement | null>(null);

watch(
  () => props.selectedGeofenceId,
  async (id) => {
    if (id == null) return;
    await nextTick();
    list.value
      ?.querySelector(`[data-testid="find-containers-row-${id}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  },
);

const rows = computed(() => {
  const scale = stockBarScale(props.sites);
  return props.sites.map((site) => ({
    site,
    distance: formatSiteDistance(site.distance_m, props.unit),
    target: formatTarget(site.min_assets, site.max_assets),
    bar: stockBar(site, scale),
  }));
});
</script>

<template>
  <ul ref="list" class="divide-y divide-gpx_gray-800">
    <li v-for="row in rows" :key="row.site.geofence_id">
      <div
        role="button"
        tabindex="0"
        class="flex w-full cursor-pointer items-start justify-between gap-4 border-l-4 px-5 py-4 text-left transition-colors"
        :class="
          row.site.geofence_id === selectedGeofenceId
            ? 'border-gpx_red-200 bg-gpx_red-300'
            : 'border-transparent hover:bg-gpx_gray-900'
        "
        :data-testid="`find-containers-row-${row.site.geofence_id}`"
        @click="emit('select', row.site)"
        @keydown.enter.self="emit('select', row.site)"
      >
        <div class="min-w-0">
          <p class="truncate text-base font-semibold text-gray-900">
            <RouterLink
              :to="{
                name: 'GeofenceDetailsView',
                params: { id: row.site.geofence_id },
              }"
              class="hover:underline"
              :data-testid="`find-containers-site-link-${row.site.geofence_id}`"
              @click.stop
            >
              {{ row.site.name }}
            </RouterLink>
          </p>
          <div class="mt-2 flex items-center gap-3">
            <div
              class="relative h-1.5 w-32 shrink-0 rounded-full bg-gpx_gray-800"
              aria-hidden="true"
            >
              <div
                class="absolute inset-y-0 left-0 rounded-full"
                :class="FILL_CLASS[row.bar.level]"
                :style="{ width: `${row.bar.fillPct}%` }"
              />
              <span
                v-for="tick in [row.bar.minPct, row.bar.maxPct].filter(
                  (p) => p != null,
                )"
                :key="tick"
                class="absolute -top-1 h-3.5 w-0.5 rounded-full bg-gpx_gray-100 -translate-x-1/2"
                :style="{ left: `${tick}%` }"
              />
            </div>
            <span class="text-sm text-gray-700">
              <span class="tabular-nums">{{ row.site.units }}</span> on site ·
              <span class="tabular-nums">{{ row.target }}</span>
            </span>
          </div>
        </div>
        <span
          class="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-gray-900"
        >
          {{ row.distance }}
        </span>
      </div>
    </li>
  </ul>
</template>
